export type AgentStatus = 'active' | 'pending' | 'disabled' | 'expired';

export interface Agent {
  id: string;
  name: string;
  email: string;
  company: string;
  region: string;
  status: AgentStatus;
  expiryDate: string;
  memberSince: string;
  totalViews: number;
  totalDownloads: number;
  lastActive: string;
  notes?: string;
  socialPlatform?: string;
  socialAccount?: string;
  permissions: {
    canDownload: boolean;
    categories: string[];
  };
  /**
   * 允许访问的产品型号列表（三级分类 model ID）。
   * undefined = 全部可见（默认）。
   * 非空数组 = 仅可见列表中的型号资料。
   * 空数组 = 无任何型号权限（pending/disabled 账号）。
   */
  allowedModels?: string[];
  /**
   * @deprecated 旧字段，保留兼容，请使用 allowedModels
   */
  allowedSubCategories?: string[];
}

export interface AdminMaterial {
  id: string;
  title: string;
  type: 'PDF' | 'PPT' | 'Video' | 'Excel' | 'Word';
  pptType?: 'training' | 'promo';
  /** 一级分类 ID，对应 categoryTree 中的 primary category */
  primaryCategory?: string;
  /** 二级分类（产品线大类，如 CT Systems） */
  category: string;
  /** 三级分类（产品型号，如 InsitumCT 338），权限授权颗粒度 */
  modelCategory?: string;
  fileSize: string;
  uploadDate: string;
  uploadedBy: string;
  uploadedByName?: string;
  views: number;
  downloads: number;
  previewAllowed: boolean;
  downloadAllowed: boolean;
  thumbnail?: string;
  isVideo?: boolean;
  videoDuration?: string;
  videoUrl?: string;
  /** 文件URL（上传后的服务器路径），视频播放使用此字段 */
  fileUrl?: string;
  description?: string;
  tags?: string[];
  confidential?: boolean;            // true = 机密，false/undefined = 普通
  confidentialAgents?: string[];     // 有权限查看的代理商 ID 列表
}

export interface ActivityPoint {
  date: string;
  views: number;
  downloads: number;
}

export const mockAgents: Agent[] = [
  { id: 'a001', name: 'James Wilson', email: 'j.wilson@medtech-global.com', company: 'MedTech Global Ltd.', region: 'Europe', status: 'active', expiryDate: '2027-04-17', memberSince: '2022-06-01', totalViews: 342, totalDownloads: 87, lastActive: '2026-04-18', notes: 'Key account in UK & Ireland market. Very active in CT Systems and Mobile CT. Renewal discussion scheduled for Q2.', permissions: { canDownload: true, categories: ['CT Systems', 'Training', 'Mobile CT'] }, allowedModels: ['InsitumCT 338', 'InsitumCT 64S', 'AlphaCT 868', 'MobileCT Pro', 'MobileCT X1'] },
  { id: 'a002', name: 'Sophie Müller', email: 's.muller@radiomed.de', company: 'RadioMed GmbH', region: 'Europe', status: 'active', expiryDate: '2027-02-28', memberSince: '2021-11-15', totalViews: 521, totalDownloads: 134, lastActive: '2026-04-19', notes: 'DACH region distributor. Strong background in radiology IT. Interested in Workstation Pro upgrade — follow up in May.', permissions: { canDownload: true, categories: ['CT Systems', 'Workstation', 'Software'] }, allowedModels: ['AlphaCT 868', 'AlphaCT 968', 'Workstation Pro 4K', 'SinoCloud Platform', 'PACS Integration'] },
  { id: 'a003', name: 'Ahmed Al-Rashid', email: 'a.rashid@gulf-imaging.ae', company: 'Gulf Imaging Solutions', region: 'Middle East', status: 'active', expiryDate: '2027-06-15', memberSince: '2023-01-20', totalViews: 198, totalDownloads: 45, lastActive: '2026-04-15', permissions: { canDownload: true, categories: ['CT Systems'] }, allowedModels: ['InsitumCT 338', 'AlphaCT 868'] },
  { id: 'a004', name: 'Yuki Tanaka', email: 'y.tanaka@medvision-jp.co.jp', company: 'MedVision Japan', region: 'East Asia', status: 'active', expiryDate: '2027-04-10', memberSince: '2026-04-10', totalViews: 12, totalDownloads: 3, lastActive: '2026-04-25', notes: 'Referred by Tokyo trade show contact. Company has good reputation in Japanese hospital network.', socialPlatform: 'LINE', socialAccount: 'yukitanaka_med', permissions: { canDownload: true, categories: ['CT Systems', 'Software'] }, allowedModels: ['InsitumCT 338', 'InsitumCT 64S', 'SinoCloud Platform'] },
  { id: 'a011', name: 'Maria Kowalski', email: 'm.kowalski@polmed.pl', company: 'PolMed Imaging', region: 'Europe', status: 'active', expiryDate: '2027-04-13', memberSince: '2026-04-13', totalViews: 8, totalDownloads: 2, lastActive: '2026-04-24', socialPlatform: 'WhatsApp', socialAccount: '+48 601 234 567', permissions: { canDownload: true, categories: ['CT Systems', 'Training'] }, allowedModels: ['InsitumCT 338', 'AlphaCT 868', 'OmegaCT One'] },
  { id: 'a005', name: 'Carlos Mendez', email: 'c.mendez@latam-health.mx', company: 'LatAm Health Tech', region: 'South America', status: 'active', expiryDate: '2026-09-30', memberSince: '2024-03-22', totalViews: 12, totalDownloads: 3, lastActive: '2026-04-05', permissions: { canDownload: true, categories: ['CT Systems'] }, allowedModels: ['InsitumCT 338', 'OmegaCT One', 'MobileCT X1'] },
  { id: 'a006', name: 'Priya Sharma', email: 'p.sharma@indomed.in', company: 'IndoMed Devices', region: 'South Asia', status: 'active', expiryDate: '2026-12-31', memberSince: '2022-03-10', totalViews: 876, totalDownloads: 210, lastActive: '2026-04-20', notes: 'Top performer in South Asia. Covers India, Sri Lanka, Bangladesh. Needs Robot Navigation access for new hospital tender in Mumbai.', permissions: { canDownload: true, categories: ['CT Systems', 'Mobile CT', 'Robot Navigation', 'Training'] }, allowedModels: undefined },
  { id: 'a007', name: 'Liam O\'Brien', email: 'l.obrien@scantech-au.com.au', company: 'ScanTech Australia', region: 'Oceania', status: 'expired', expiryDate: '2026-01-31', memberSince: '2021-07-05', totalViews: 654, totalDownloads: 98, lastActive: '2026-01-28', notes: 'Account expired — awaiting renewal confirmation. Company undergoing internal restructuring. Check back end of April.', permissions: { canDownload: false, categories: [] }, allowedModels: [] },
  { id: 'a008', name: 'Fatima Nkosi', email: 'f.nkosi@afrimed.co.za', company: 'AfriMed Solutions', region: 'Africa', status: 'disabled', expiryDate: '2026-06-30', memberSince: '2024-02-14', totalViews: 0, totalDownloads: 0, lastActive: '—', notes: 'Account disabled due to non-payment of annual distributor fee. Do not reactivate until finance confirms receipt.', permissions: { canDownload: false, categories: [] }, allowedModels: [] },
  { id: 'a009', name: 'Erik Lindqvist', email: 'e.lindqvist@nordic-imaging.se', company: 'Nordic Imaging AB', region: 'Europe', status: 'active', expiryDate: '2027-03-01', memberSince: '2020-09-12', totalViews: 1203, totalDownloads: 312, lastActive: '2026-04-21', permissions: { canDownload: true, categories: ['CT Systems', 'Software', 'Workstation', 'Training'] }, allowedModels: undefined },
  { id: 'a010', name: 'Wei Zhang', email: 'w.zhang@sino-dist.com', company: 'Sino Distribution Co.', region: 'East Asia', status: 'active', expiryDate: '2026-09-15', memberSince: '2023-05-01', totalViews: 445, totalDownloads: 102, lastActive: '2026-04-17', permissions: { canDownload: true, categories: ['CT Systems', 'Mobile CT'] }, allowedModels: ['InsitumCT 338', 'InsitumCT 64S', 'AlphaCT 868', 'MobileCT X1', 'MobileCT Pro'] },
];

export const mockAdminMaterials: AdminMaterial[] = [
  { id: 'm001', title: 'InsitumCT 338 Product Brochure 2024', type: 'PDF', primaryCategory: 'products', category: 'CT Systems', modelCategory: 'InsitumCT 338', fileSize: '8.4 MB', uploadDate: '2026-03-15', uploadedBy: 'Admin', views: 1240, downloads: 387, previewAllowed: true, downloadAllowed: true, confidential: false, confidentialAgents: [], thumbnail: '' },
  { id: 'm002', title: 'AlphaCT 868 Technical Specifications', type: 'PDF', primaryCategory: 'products', category: 'CT Systems', modelCategory: 'AlphaCT 868', fileSize: '3.2 MB', uploadDate: '2026-02-28', uploadedBy: 'Admin', views: 876, downloads: 201, previewAllowed: true, downloadAllowed: true, confidential: false, confidentialAgents: [], thumbnail: '' },
  { id: 'm003', title: 'SinoCloud Platform Training Slides (PDF)', type: 'PPT', pptType: 'training', primaryCategory: 'software', category: 'Software', modelCategory: 'SinoCloud Platform', fileSize: '12.7 MB', uploadDate: '2026-03-01', uploadedBy: 'Marketing', views: 2103, downloads: 0, previewAllowed: true, downloadAllowed: false, confidential: false, confidentialAgents: [], thumbnail: '' },
  { id: 'm004', title: 'InsitumCT 64S Operation Manual v2.1', type: 'PDF', primaryCategory: 'products', category: 'CT Systems', modelCategory: 'InsitumCT 64S', fileSize: '24.1 MB', uploadDate: '2026-01-20', uploadedBy: 'Admin', views: 654, downloads: 145, previewAllowed: true, downloadAllowed: true, confidential: false, confidentialAgents: [], thumbnail: '' },
  { id: 'm005', title: 'PACS Integration Guide', type: 'PDF', primaryCategory: 'software', category: 'Software', modelCategory: 'PACS Integration', fileSize: '5.6 MB', uploadDate: '2026-03-10', uploadedBy: 'Tech', views: 432, downloads: 0, previewAllowed: true, downloadAllowed: false, confidential: false, confidentialAgents: [], thumbnail: '' },
  { id: 'm006', title: 'MobileCT Pro Sales Deck Q1 2024', type: 'PPT', pptType: 'promo', primaryCategory: 'products', category: 'Mobile CT', modelCategory: 'MobileCT Pro', fileSize: '18.3 MB', uploadDate: '2026-03-05', uploadedBy: 'Marketing', views: 1876, downloads: 423, previewAllowed: true, downloadAllowed: true, confidential: false, confidentialAgents: [], thumbnail: '' },
  { id: 'm007', title: 'RoboNav 3.0 Clinical Application Guide', type: 'PDF', primaryCategory: 'products', category: 'Robot Navigation', modelCategory: 'RoboNav 3.0', fileSize: '9.8 MB', uploadDate: '2026-02-14', uploadedBy: 'Admin', views: 1342, downloads: 298, previewAllowed: true, downloadAllowed: true, confidential: true, confidentialAgents: ['a001', 'a006', 'a009'], thumbnail: '' },
  { id: 'm008', title: 'Workstation Pro 4K Setup & Configuration', type: 'PDF', primaryCategory: 'products', category: 'Workstation', modelCategory: 'Workstation Pro 4K', fileSize: '6.2 MB', uploadDate: '2026-01-30', uploadedBy: 'Tech', views: 987, downloads: 312, previewAllowed: true, downloadAllowed: true, confidential: true, confidentialAgents: ['a002', 'a009'], thumbnail: '' },
  { id: 'm009', title: 'PetCare CT V1 Veterinary Application Guide', type: 'PDF', primaryCategory: 'products', category: 'Veterinary', modelCategory: 'PetCare CT V1', fileSize: '7.1 MB', uploadDate: '2026-03-08', uploadedBy: 'Marketing', views: 743, downloads: 189, previewAllowed: true, downloadAllowed: true, confidential: false, confidentialAgents: [], thumbnail: '' },
  { id: 'm010', title: 'Distributor Training Handbook 2024 Edition', type: 'PDF', primaryCategory: 'training', category: 'Training', fileSize: '15.4 MB', uploadDate: '2026-03-12', uploadedBy: 'Admin', views: 521, downloads: 134, previewAllowed: true, downloadAllowed: true, confidential: true, confidentialAgents: ['a001', 'a002', 'a006', 'a009', 'a010'], thumbnail: '' },
  { id: 'm011', title: 'OmegaCT One Launch Presentation 2024', type: 'PPT', pptType: 'promo', primaryCategory: 'products', category: 'CT Systems', modelCategory: 'OmegaCT One', fileSize: '22.5 MB', uploadDate: '2026-02-20', uploadedBy: 'Marketing', views: 3241, downloads: 876, previewAllowed: true, downloadAllowed: true, confidential: false, confidentialAgents: [], thumbnail: '' },
  { id: 'm012', title: 'AlphaCT 968 Competitive Analysis Report', type: 'PDF', primaryCategory: 'products', category: 'CT Systems', modelCategory: 'AlphaCT 968', fileSize: '6.1 MB', uploadDate: '2026-01-18', uploadedBy: 'Admin', views: 345, downloads: 0, previewAllowed: true, downloadAllowed: false, confidential: true, confidentialAgents: ['a001', 'a002', 'a009'], thumbnail: '' },
];

export const mockActivityData: ActivityPoint[] = [
  { date: '2025-04-05', views: 98, downloads: 22 },
  { date: '2025-04-06', views: 112, downloads: 28 },
  { date: '2025-04-07', views: 87, downloads: 19 },
  { date: '2025-04-08', views: 134, downloads: 35 },
  { date: '2025-04-09', views: 145, downloads: 38 },
  { date: '2025-04-10', views: 121, downloads: 31 },
  { date: '2025-04-11', views: 103, downloads: 25 },
  { date: '2025-04-12', views: 156, downloads: 41 },
  { date: '2025-04-13', views: 167, downloads: 44 },
  { date: '2025-04-14', views: 143, downloads: 37 },
  { date: '2025-04-15', views: 189, downloads: 52 },
  { date: '2025-04-16', views: 201, downloads: 57 },
  { date: '2025-04-17', views: 178, downloads: 48 },
  { date: '2025-04-18', views: 165, downloads: 45 },
  { date: '2025-04-19', views: 212, downloads: 59 },
  { date: '2025-04-20', views: 234, downloads: 64 },
  { date: '2025-04-21', views: 198, downloads: 55 },
  { date: '2025-04-22', views: 187, downloads: 51 },
  { date: '2025-04-23', views: 221, downloads: 62 },
  { date: '2025-04-24', views: 245, downloads: 68 },
  { date: '2025-04-25', views: 267, downloads: 74 },
  { date: '2025-04-26', views: 243, downloads: 67 },
  { date: '2025-04-27', views: 256, downloads: 71 },
  { date: '2025-04-28', views: 234, downloads: 64 },
  { date: '2025-04-29', views: 278, downloads: 77 },
  { date: '2025-04-30', views: 289, downloads: 80 },
  { date: '2025-05-01', views: 198, downloads: 54 },
  { date: '2025-05-02', views: 212, downloads: 58 },
  { date: '2025-05-03', views: 234, downloads: 65 },
  { date: '2025-05-04', views: 256, downloads: 70 },
  { date: '2025-05-05', views: 245, downloads: 67 },
  { date: '2025-05-06', views: 267, downloads: 73 },
  { date: '2025-05-07', views: 289, downloads: 79 },
  { date: '2025-05-08', views: 312, downloads: 86 },
  { date: '2025-05-09', views: 298, downloads: 82 },
  { date: '2025-05-10', views: 323, downloads: 89 },
  { date: '2025-05-11', views: 301, downloads: 83 },
  { date: '2025-05-12', views: 334, downloads: 92 },
  { date: '2025-05-13', views: 356, downloads: 98 },
  { date: '2025-05-14', views: 312, downloads: 86 },
  { date: '2025-05-15', views: 345, downloads: 95 },
  { date: '2025-05-16', views: 367, downloads: 101 },
  { date: '2025-05-17', views: 378, downloads: 104 },
  { date: '2025-05-18', views: 345, downloads: 95 },
  { date: '2025-05-19', views: 389, downloads: 107 },
  { date: '2025-05-20', views: 401, downloads: 110 },
  { date: '2025-05-21', views: 378, downloads: 104 },
  { date: '2025-05-22', views: 412, downloads: 113 },
  { date: '2025-05-23', views: 398, downloads: 109 },
  { date: '2025-05-24', views: 423, downloads: 116 },
  { date: '2025-05-25', views: 434, downloads: 119 },
  { date: '2025-05-26', views: 412, downloads: 113 },
  { date: '2025-05-27', views: 445, downloads: 122 },
  { date: '2025-05-28', views: 423, downloads: 116 },
  { date: '2025-05-29', views: 456, downloads: 125 },
  { date: '2025-05-30', views: 467, downloads: 128 },
  { date: '2025-05-31', views: 445, downloads: 122 },
  { date: '2025-06-01', views: 312, downloads: 85 },
  { date: '2025-06-02', views: 334, downloads: 92 },
  { date: '2025-06-03', views: 356, downloads: 98 },
  { date: '2025-06-04', views: 378, downloads: 104 },
  { date: '2025-06-05', views: 367, downloads: 101 },
  { date: '2025-06-06', views: 389, downloads: 107 },
  { date: '2025-06-07', views: 401, downloads: 110 },
  { date: '2025-06-08', views: 412, downloads: 113 },
  { date: '2025-06-09', views: 398, downloads: 109 },
  { date: '2025-06-10', views: 423, downloads: 116 },
  { date: '2025-06-11', views: 412, downloads: 113 },
  { date: '2025-06-12', views: 434, downloads: 119 },
  { date: '2025-06-13', views: 445, downloads: 122 },
  { date: '2025-06-14', views: 423, downloads: 116 },
  { date: '2025-06-15', views: 456, downloads: 125 },
  { date: '2025-06-16', views: 467, downloads: 128 },
  { date: '2025-06-17', views: 445, downloads: 122 },
  { date: '2025-06-18', views: 478, downloads: 131 },
  { date: '2025-06-19', views: 456, downloads: 125 },
  { date: '2025-06-20', views: 489, downloads: 134 },
  { date: '2025-06-21', views: 478, downloads: 131 },
  { date: '2025-06-22', views: 512, downloads: 141 },
  { date: '2025-06-23', views: 498, downloads: 137 },
  { date: '2025-06-24', views: 523, downloads: 144 },
  { date: '2025-06-25', views: 534, downloads: 147 },
  { date: '2025-06-26', views: 512, downloads: 141 },
  { date: '2025-06-27', views: 545, downloads: 150 },
  { date: '2025-06-28', views: 523, downloads: 144 },
  { date: '2025-06-29', views: 556, downloads: 153 },
  { date: '2025-06-30', views: 567, downloads: 156 },
  { date: '2025-07-01', views: 456, downloads: 125 },
  { date: '2025-07-02', views: 478, downloads: 131 },
  { date: '2025-07-03', views: 501, downloads: 138 },
  { date: '2025-07-04', views: 489, downloads: 134 },
  { date: '2025-07-05', views: 512, downloads: 141 },
  { date: '2025-07-06', views: 498, downloads: 137 },
  { date: '2025-07-07', views: 523, downloads: 144 },
  { date: '2025-07-08', views: 545, downloads: 150 },
  { date: '2025-07-09', views: 523, downloads: 144 },
  { date: '2025-07-10', views: 556, downloads: 153 },
  { date: '2025-07-11', views: 567, downloads: 156 },
  { date: '2025-07-12', views: 578, downloads: 159 },
  { date: '2025-07-13', views: 556, downloads: 153 },
  { date: '2025-07-14', views: 589, downloads: 162 },
  { date: '2025-07-15', views: 601, downloads: 165 },
  { date: '2025-07-16', views: 578, downloads: 159 },
  { date: '2025-07-17', views: 612, downloads: 168 },
  { date: '2025-07-18', views: 598, downloads: 164 },
  { date: '2025-07-19', views: 623, downloads: 171 },
  { date: '2025-07-20', views: 634, downloads: 174 },
  { date: '2025-07-21', views: 612, downloads: 168 },
  { date: '2025-07-22', views: 645, downloads: 177 },
  { date: '2025-07-23', views: 623, downloads: 171 },
  { date: '2025-07-24', views: 656, downloads: 180 },
  { date: '2025-07-25', views: 667, downloads: 183 },
  { date: '2025-07-26', views: 645, downloads: 177 },
  { date: '2025-07-27', views: 678, downloads: 186 },
  { date: '2025-07-28', views: 656, downloads: 180 },
  { date: '2025-07-29', views: 689, downloads: 189 },
  { date: '2025-07-30', views: 701, downloads: 193 },
  { date: '2025-07-31', views: 678, downloads: 186 },
  { date: '2025-08-01', views: 534, downloads: 147 },
  { date: '2025-08-02', views: 556, downloads: 153 },
  { date: '2025-08-03', views: 578, downloads: 159 },
  { date: '2025-08-04', views: 567, downloads: 156 },
  { date: '2025-08-05', views: 589, downloads: 162 },
  { date: '2025-08-06', views: 601, downloads: 165 },
  { date: '2025-08-07', views: 578, downloads: 159 },
  { date: '2025-08-08', views: 612, downloads: 168 },
  { date: '2025-08-09', views: 623, downloads: 171 },
  { date: '2025-08-10', views: 601, downloads: 165 },
  { date: '2025-08-11', views: 634, downloads: 174 },
  { date: '2025-08-12', views: 645, downloads: 177 },
  { date: '2025-08-13', views: 623, downloads: 171 },
  { date: '2025-08-14', views: 656, downloads: 180 },
  { date: '2025-08-15', views: 667, downloads: 183 },
  { date: '2025-08-16', views: 645, downloads: 177 },
  { date: '2025-08-17', views: 678, downloads: 186 },
  { date: '2025-08-18', views: 656, downloads: 180 },
  { date: '2025-08-19', views: 689, downloads: 189 },
  { date: '2025-08-20', views: 701, downloads: 193 },
  { date: '2025-08-21', views: 678, downloads: 186 },
  { date: '2025-08-22', views: 712, downloads: 196 },
  { date: '2025-08-23', views: 698, downloads: 192 },
  { date: '2025-08-24', views: 723, downloads: 199 },
  { date: '2025-08-25', views: 734, downloads: 202 },
  { date: '2025-08-26', views: 712, downloads: 196 },
  { date: '2025-08-27', views: 745, downloads: 205 },
  { date: '2025-08-28', views: 723, downloads: 199 },
  { date: '2025-08-29', views: 756, downloads: 208 },
  { date: '2025-08-30', views: 767, downloads: 211 },
  { date: '2025-08-31', views: 678, downloads: 186 },
  { date: '2025-09-01', views: 534, downloads: 147 },
  { date: '2025-09-02', views: 556, downloads: 153 },
  { date: '2025-09-03', views: 578, downloads: 159 },
  { date: '2025-09-04', views: 567, downloads: 156 },
  { date: '2025-09-05', views: 589, downloads: 162 },
  { date: '2025-09-06', views: 601, downloads: 165 },
  { date: '2025-09-07', views: 578, downloads: 159 },
  { date: '2025-09-08', views: 612, downloads: 168 },
  { date: '2025-09-09', views: 623, downloads: 171 },
  { date: '2025-09-10', views: 601, downloads: 165 },
  { date: '2025-09-11', views: 634, downloads: 174 },
  { date: '2025-09-12', views: 645, downloads: 177 },
  { date: '2025-09-13', views: 623, downloads: 171 },
  { date: '2025-09-14', views: 656, downloads: 180 },
  { date: '2025-09-15', views: 667, downloads: 183 },
  { date: '2025-09-16', views: 645, downloads: 177 },
  { date: '2025-09-17', views: 678, downloads: 186 },
  { date: '2025-09-18', views: 656, downloads: 180 },
  { date: '2025-09-19', views: 689, downloads: 189 },
  { date: '2025-09-20', views: 701, downloads: 193 },
  { date: '2025-09-21', views: 678, downloads: 186 },
  { date: '2025-09-22', views: 712, downloads: 196 },
  { date: '2025-09-23', views: 698, downloads: 192 },
  { date: '2025-09-24', views: 723, downloads: 199 },
  { date: '2025-09-25', views: 734, downloads: 202 },
  { date: '2025-09-26', views: 712, downloads: 196 },
  { date: '2025-09-27', views: 745, downloads: 205 },
  { date: '2025-09-28', views: 723, downloads: 199 },
  { date: '2025-09-29', views: 756, downloads: 208 },
  { date: '2025-09-30', views: 767, downloads: 211 },
  { date: '2025-10-01', views: 612, downloads: 168 },
  { date: '2025-10-02', views: 634, downloads: 174 },
  { date: '2025-10-03', views: 656, downloads: 180 },
  { date: '2025-10-04', views: 645, downloads: 177 },
  { date: '2025-10-05', views: 667, downloads: 183 },
  { date: '2025-10-06', views: 689, downloads: 189 },
  { date: '2025-10-07', views: 678, downloads: 186 },
  { date: '2025-10-08', views: 701, downloads: 193 },
  { date: '2025-10-09', views: 712, downloads: 196 },
  { date: '2025-10-10', views: 689, downloads: 189 },
  { date: '2025-10-11', views: 723, downloads: 199 },
  { date: '2025-10-12', views: 734, downloads: 202 },
  { date: '2025-10-13', views: 712, downloads: 196 },
  { date: '2025-10-14', views: 745, downloads: 205 },
  { date: '2025-10-15', views: 756, downloads: 208 },
  { date: '2025-10-16', views: 734, downloads: 202 },
  { date: '2025-10-17', views: 767, downloads: 211 },
  { date: '2025-10-18', views: 745, downloads: 205 },
  { date: '2025-10-19', views: 778, downloads: 214 },
  { date: '2025-10-20', views: 789, downloads: 217 },
  { date: '2025-10-21', views: 767, downloads: 211 },
  { date: '2025-10-22', views: 801, downloads: 220 },
  { date: '2025-10-23', views: 789, downloads: 217 },
  { date: '2025-10-24', views: 812, downloads: 223 },
  { date: '2025-10-25', views: 823, downloads: 226 },
  { date: '2025-10-26', views: 801, downloads: 220 },
  { date: '2025-10-27', views: 834, downloads: 229 },
  { date: '2025-10-28', views: 812, downloads: 223 },
  { date: '2025-10-29', views: 845, downloads: 232 },
  { date: '2025-10-30', views: 856, downloads: 235 },
  { date: '2025-10-31', views: 834, downloads: 229 },
  { date: '2025-11-01', views: 712, downloads: 196 },
  { date: '2025-11-02', views: 734, downloads: 202 },
  { date: '2025-11-03', views: 756, downloads: 208 },
  { date: '2025-11-04', views: 745, downloads: 205 },
  { date: '2025-11-05', views: 767, downloads: 211 },
  { date: '2025-11-06', views: 789, downloads: 217 },
  { date: '2025-11-07', views: 778, downloads: 214 },
  { date: '2025-11-08', views: 801, downloads: 220 },
  { date: '2025-11-09', views: 812, downloads: 223 },
  { date: '2025-11-10', views: 789, downloads: 217 },
  { date: '2025-11-11', views: 823, downloads: 226 },
  { date: '2025-11-12', views: 834, downloads: 229 },
  { date: '2025-11-13', views: 812, downloads: 223 },
  { date: '2025-11-14', views: 845, downloads: 232 },
  { date: '2025-11-15', views: 856, downloads: 235 },
  { date: '2025-11-16', views: 834, downloads: 229 },
  { date: '2025-11-17', views: 867, downloads: 238 },
  { date: '2025-11-18', views: 845, downloads: 232 },
  { date: '2025-11-19', views: 878, downloads: 241 },
  { date: '2025-11-20', views: 889, downloads: 244 },
  { date: '2025-11-21', views: 867, downloads: 238 },
  { date: '2025-11-22', views: 901, downloads: 251 },
  { date: '2025-11-23', views: 878, downloads: 241 },
  { date: '2025-11-24', views: 856, downloads: 235 },
  { date: '2025-11-25', views: 823, downloads: 226 },
  { date: '2025-11-26', views: 812, downloads: 223 },
  { date: '2025-11-27', views: 834, downloads: 229 },
  { date: '2025-11-28', views: 845, downloads: 232 },
  { date: '2025-11-29', views: 867, downloads: 238 },
  { date: '2025-11-30', views: 878, downloads: 241 },
  { date: '2025-12-01', views: 756, downloads: 208 },
  { date: '2025-12-02', views: 778, downloads: 214 },
  { date: '2025-12-03', views: 801, downloads: 220 },
  { date: '2025-12-04', views: 789, downloads: 217 },
  { date: '2025-12-05', views: 812, downloads: 223 },
  { date: '2025-12-06', views: 834, downloads: 229 },
  { date: '2025-12-07', views: 823, downloads: 226 },
  { date: '2025-12-08', views: 845, downloads: 232 },
  { date: '2025-12-09', views: 856, downloads: 235 },
  { date: '2025-12-10', views: 834, downloads: 229 },
  { date: '2025-12-11', views: 867, downloads: 238 },
  { date: '2025-12-12', views: 878, downloads: 241 },
  { date: '2025-12-13', views: 856, downloads: 235 },
  { date: '2025-12-14', views: 889, downloads: 244 },
  { date: '2025-12-15', views: 901, downloads: 248 },
  { date: '2025-12-16', views: 878, downloads: 241 },
  { date: '2025-12-17', views: 912, downloads: 251 },
  { date: '2025-12-18', views: 889, downloads: 244 },
  { date: '2025-12-19', views: 923, downloads: 254 },
  { date: '2025-12-20', views: 934, downloads: 257 },
  { date: '2025-12-21', views: 912, downloads: 251 },
  { date: '2025-12-22', views: 945, downloads: 260 },
  { date: '2025-12-23', views: 923, downloads: 254 },
  { date: '2025-12-24', views: 956, downloads: 263 },
  { date: '2025-12-25', views: 967, downloads: 266 },
  { date: '2025-12-26', views: 945, downloads: 260 },
  { date: '2025-12-27', views: 978, downloads: 269 },
  { date: '2025-12-28', views: 956, downloads: 263 },
  { date: '2025-12-29', views: 989, downloads: 272 },
  { date: '2025-12-30', views: 1001, downloads: 275 },
  { date: '2025-12-31', views: 978, downloads: 269 },
  { date: '2026-01-01', views: 867, downloads: 238 },
  { date: '2026-01-02', views: 889, downloads: 244 },
  { date: '2026-01-03', views: 901, downloads: 248 },
  { date: '2026-01-04', views: 912, downloads: 251 },
  { date: '2026-01-05', views: 934, downloads: 257 },
  { date: '2026-01-06', views: 945, downloads: 260 },
  { date: '2026-01-07', views: 923, downloads: 254 },
  { date: '2026-01-08', views: 956, downloads: 263 },
  { date: '2026-01-09', views: 967, downloads: 266 },
  { date: '2026-01-10', views: 945, downloads: 260 },
  { date: '2026-01-11', views: 978, downloads: 269 },
  { date: '2026-01-12', views: 989, downloads: 272 },
  { date: '2026-01-13', views: 967, downloads: 266 },
  { date: '2026-01-14', views: 1001, downloads: 275 },
  { date: '2026-01-15', views: 1012, downloads: 278 },
  { date: '2026-01-16', views: 989, downloads: 272 },
  { date: '2026-01-17', views: 1023, downloads: 281 },
  { date: '2026-01-18', views: 1001, downloads: 275 },
  { date: '2026-01-19', views: 1034, downloads: 281 },
  { date: '2026-01-20', views: 1045, downloads: 287 },
  { date: '2026-01-21', views: 1023, downloads: 281 },
  { date: '2026-01-22', views: 1056, downloads: 290 },
  { date: '2026-01-23', views: 1034, downloads: 284 },
  { date: '2026-01-24', views: 1067, downloads: 293 },
  { date: '2026-01-25', views: 1078, downloads: 296 },
  { date: '2026-01-26', views: 1056, downloads: 290 },
  { date: '2026-01-27', views: 1089, downloads: 299 },
  { date: '2026-01-28', views: 1067, downloads: 293 },
  { date: '2026-01-29', views: 1078, downloads: 296 },
  { date: '2026-01-30', views: 1089, downloads: 299 },
  { date: '2026-01-31', views: 1067, downloads: 293 },
  { date: '2026-02-01', views: 1078, downloads: 296 },
  { date: '2026-02-02', views: 1056, downloads: 290 },
  { date: '2026-02-03', views: 1089, downloads: 299 },
  { date: '2026-02-04', views: 1101, downloads: 303 },
  { date: '2026-02-05', views: 1078, downloads: 296 },
  { date: '2026-02-06', views: 1112, downloads: 306 },
  { date: '2026-02-07', views: 1123, downloads: 309 },
  { date: '2026-02-08', views: 1101, downloads: 303 },
  { date: '2026-02-09', views: 1134, downloads: 312 },
  { date: '2026-02-10', views: 1156, downloads: 318 },
  { date: '2026-02-11', views: 1134, downloads: 312 },
  { date: '2026-02-12', views: 1167, downloads: 321 },
  { date: '2026-02-13', views: 1145, downloads: 315 },
  { date: '2026-02-14', views: 1167, downloads: 321 },
  { date: '2026-02-15', views: 1189, downloads: 327 },
  { date: '2026-02-16', views: 1189, downloads: 327 },
  { date: '2026-02-17', views: 1178, downloads: 324 },
  { date: '2026-02-18', views: 1156, downloads: 318 },
  { date: '2026-02-19', views: 1189, downloads: 327 },
  { date: '2026-02-20', views: 1201, downloads: 330 },
  { date: '2026-02-21', views: 1178, downloads: 324 },
  { date: '2026-02-22', views: 1212, downloads: 333 },
  { date: '2026-02-23', views: 1223, downloads: 336 },
  { date: '2026-02-24', views: 1201, downloads: 330 },
  { date: '2026-02-25', views: 1189, downloads: 327 },
  { date: '2026-02-26', views: 1212, downloads: 333 },
  { date: '2026-02-27', views: 1223, downloads: 336 },
  { date: '2026-02-28', views: 1201, downloads: 330 },
  { date: '2026-03-01', views: 1223, downloads: 336 },
  { date: '2026-03-02', views: 1201, downloads: 330 },
  { date: '2026-03-03', views: 1189, downloads: 327 },
  { date: '2026-03-04', views: 1178, downloads: 324 },
  { date: '2026-03-05', views: 1156, downloads: 318 },
  { date: '2026-03-06', views: 1134, downloads: 312 },
  { date: '2026-03-07', views: 1167, downloads: 321 },
  { date: '2026-03-08', views: 1145, downloads: 315 },
  { date: '2026-03-09', views: 1178, downloads: 324 },
  { date: '2026-03-10', views: 1156, downloads: 318 },
  { date: '2026-03-11', views: 1189, downloads: 327 },
  { date: '2026-03-12', views: 1201, downloads: 330 },
  { date: '2026-03-13', views: 1189, downloads: 327 },
  { date: '2026-03-14', views: 1212, downloads: 333 },
  { date: '2026-03-15', views: 1223, downloads: 336 },
  { date: '2026-03-16', views: 1201, downloads: 330 },
  { date: '2026-03-17', views: 1234, downloads: 339 },
  { date: '2026-03-18', views: 1245, downloads: 342 },
  { date: '2026-03-19', views: 1267, downloads: 348 },
  { date: '2026-03-20', views: 1245, downloads: 342 },
  { date: '2026-03-21', views: 1267, downloads: 348 },
  { date: '2026-03-22', views: 1289, downloads: 351 },
  { date: '2026-03-23', views: 1267, downloads: 348 },
  { date: '2026-03-24', views: 1312, downloads: 354 },
  { date: '2026-03-25', views: 1289, downloads: 351 },
  { date: '2026-03-26', views: 1334, downloads: 357 },
  { date: '2026-03-27', views: 1312, downloads: 354 },
  { date: '2026-03-28', views: 1345, downloads: 358 },
  { date: '2026-03-29', views: 1334, downloads: 357 },
  { date: '2026-03-30', views: 1367, downloads: 360 },
  { date: '2026-03-31', views: 1345, downloads: 358 },
  { date: '2026-04-01', views: 1367, downloads: 360 },
  { date: '2026-04-02', views: 1389, downloads: 363 },
  { date: '2026-04-03', views: 1367, downloads: 360 },
  { date: '2026-04-04', views: 1389, downloads: 363 },
  { date: '2026-04-05', views: 1412, downloads: 366 },
];

export const mockKPIs = {
  totalAgents: 10,
  activeAgents: 8,
  disabledAgents: 1,
  totalDownloads: 1974,
  totalViews: 8390,
  totalMaterials: 12,
};

export const mockUserStatusData = [
  { label: 'Active', value: 8, color: '#22c55e' },
  { label: 'Pending', value: 0, color: '#f59e0b' },
  { label: 'Disabled', value: 1, color: '#9ca3af' },
  { label: 'Expired', value: 1, color: '#ef4444' },
];

export const mockMaterialCategoryData = [
  { label: 'CT Systems', value: 5, color: '#0069BD' },
  { label: 'E-learning', value: 5, color: '#8b5cf6' },
  { label: 'Software', value: 2, color: '#06b6d4' },
  { label: 'Mobile CT', value: 1, color: '#f97316' },
  { label: 'Robot Navigation', value: 1, color: '#ec4899' },
  { label: 'Workstation', value: 1, color: '#10b981' },
  { label: 'Training', value: 1, color: '#f59e0b' },
  { label: 'Veterinary', value: 1, color: '#84cc16' },
];

/**
 * 资料分类层级结构
 * 一级分类（primaryCategory）→ 二级分类（subCategory）→ 三级分类（modelCategory，产品型号）
 * 权限授权颗粒度为三级分类（产品型号）
 */
export interface ModelNode {
  id: string;
  label: string;
}

export interface SubCategoryNode {
  id: string;
  label: string;
  icon: string;
  /** 三级分类（产品型号列表），无型号的分类留空数组 */
  models: ModelNode[];
}

export interface CategoryNode {
  id: string;
  label: string;
  /** i18n translation key for the primary category label */
  tKey: string;
  icon: string;
  color: string;
  subCategories: SubCategoryNode[];
}

export const categoryTree: CategoryNode[] = [
  {
    id: 'products',
    label: '产品线',
    tKey: 'primary_products',
    icon: 'ri-box-3-line',
    color: '#0069BD',
    subCategories: [
      {
        id: 'CT Systems',
        label: 'CT Systems',
        icon: 'ri-scan-line',
        models: [
          { id: 'InsitumCT 338', label: 'InsitumCT 338' },
          { id: 'InsitumCT 64S', label: 'InsitumCT 64S' },
          { id: 'AlphaCT 868', label: 'AlphaCT 868' },
          { id: 'AlphaCT 968', label: 'AlphaCT 968' },
          { id: 'OmegaCT One', label: 'OmegaCT One' },
        ],
      },
      {
        id: 'Mobile CT',
        label: 'Mobile CT',
        icon: 'ri-car-line',
        models: [
          { id: 'MobileCT X1', label: 'MobileCT X1' },
          { id: 'MobileCT Pro', label: 'MobileCT Pro' },
        ],
      },
      {
        id: 'Robot Navigation',
        label: 'Robot Navigation',
        icon: 'ri-robot-line',
        models: [
          { id: 'RoboNav 3.0', label: 'RoboNav 3.0' },
          { id: 'RoboNav Lite', label: 'RoboNav Lite' },
        ],
      },
      {
        id: 'Workstation',
        label: 'Workstation',
        icon: 'ri-computer-line',
        models: [
          { id: 'Workstation Pro 4K', label: 'Workstation Pro 4K' },
          { id: 'Workstation Standard', label: 'Workstation Standard' },
        ],
      },
      {
        id: 'Veterinary',
        label: 'Veterinary',
        icon: 'ri-heart-pulse-line',
        models: [
          { id: 'PetCare CT V1', label: 'PetCare CT V1' },
        ],
      },
    ],
  },
  {
    id: 'training',
    label: '培训与学习',
    tKey: 'primary_training',
    icon: 'ri-graduation-cap-line',
    color: '#7c3aed',
    subCategories: [
      { id: 'E-learning', label: 'E-learning', icon: 'ri-video-line', models: [] },
      { id: 'Training', label: 'Training', icon: 'ri-book-open-line', models: [] },
    ],
  },
  {
    id: 'software',
    label: '软件与系统',
    tKey: 'primary_software',
    icon: 'ri-code-box-line',
    color: '#0891b2',
    subCategories: [
      {
        id: 'Software',
        label: 'Software',
        icon: 'ri-code-box-line',
        models: [
          { id: 'SinoCloud Platform', label: 'SinoCloud Platform' },
          { id: 'PACS Integration', label: 'PACS Integration' },
        ],
      },
    ],
  },
];

/** 所有三级分类 ID 列表（用于全选/权限判断） */
export const allModelIds: string[] = categoryTree.flatMap((c) =>
  c.subCategories.flatMap((s) => s.models.map((m) => m.id))
);

/** 所有二级分类 ID 列表 */
export const allSubCategories: string[] = categoryTree.flatMap((c) => c.subCategories.map((s) => s.id));

/** 兼容旧代码，保留 allCategories 指向所有二级分类 */
export const allCategories = allSubCategories;

/** 根据三级分类 ID 找到其所属的二级分类和一级分类 */
export function findCategoryByModel(modelId: string): { primary: CategoryNode; sub: SubCategoryNode; model: ModelNode } | null {
  for (const primary of categoryTree) {
    for (const sub of primary.subCategories) {
      const model = sub.models.find((m) => m.id === modelId);
      if (model) return { primary, sub, model };
    }
  }
  return null;
}

/** 根据二级分类 ID 找一级分类 */
export function findPrimaryBySubId(subId: string): CategoryNode | undefined {
  return categoryTree.find((c) => c.subCategories.some((s) => s.id === subId));
}

export type ActivityActionType = 'download' | 'create' | 'update' | 'delete' | 'view' | 'enable' | 'disable' | 'renew';
export type ActivityTargetType = 'agent' | 'material';

export interface RecentActivity {
  id: string;
  actor_name: string;
  actor_id: string | null;
  action_type: ActivityActionType;
  target_type: ActivityTargetType;
  target_name: string;
  target_id: string;
  timestamp: string;
}

export const mockRecentActivities: RecentActivity[] = [
  { id: 'act001', actor_name: 'Admin', actor_id: null, action_type: 'create', target_type: 'agent', target_name: 'Yuki Tanaka', target_id: 'a004', timestamp: '2026-04-06T09:42:00Z' },
  { id: 'act002', actor_name: 'Erik Lindqvist', actor_id: 'a009', action_type: 'download', target_type: 'material', target_name: 'InsitumCT 338 Product Brochure 2024', target_id: 'm001', timestamp: '2026-04-06T09:15:00Z' },
  { id: 'act003', actor_name: 'Admin', actor_id: null, action_type: 'update', target_type: 'material', target_name: 'AlphaCT 868 Technical Specifications', target_id: 'm002', timestamp: '2026-04-06T08:58:00Z' },
  { id: 'act004', actor_name: 'Priya Sharma', actor_id: 'a006', action_type: 'download', target_type: 'material', target_name: 'Mobile CT Solution Sales Deck Q1 2024', target_id: 'm006', timestamp: '2026-04-06T08:30:00Z' },
  { id: 'act005', actor_name: 'Admin', actor_id: null, action_type: 'enable', target_type: 'agent', target_name: 'Carlos Mendez', target_id: 'a005', timestamp: '2026-04-06T08:10:00Z' },
  { id: 'act006', actor_name: 'Wei Zhang', actor_id: 'a010', action_type: 'download', target_type: 'material', target_name: 'Insitum 64S Operation Manual v2.1', target_id: 'm004', timestamp: '2026-04-05T17:45:00Z' },
  { id: 'act007', actor_name: 'Admin', actor_id: null, action_type: 'delete', target_type: 'material', target_name: 'Old_Catalog_2022.pdf', target_id: 'm_old', timestamp: '2026-04-05T16:20:00Z' },
  { id: 'act008', actor_name: 'Sophie Müller', actor_id: 'a002', action_type: 'download', target_type: 'material', target_name: 'PACS Integration Guide', target_id: 'm005', timestamp: '2026-04-05T15:55:00Z' },
  { id: 'act009', actor_name: 'Admin', actor_id: null, action_type: 'renew', target_type: 'agent', target_name: 'James Wilson', target_id: 'a001', timestamp: '2026-04-05T14:30:00Z' },
  { id: 'act010', actor_name: 'Ahmed Al-Rashid', actor_id: 'a003', action_type: 'download', target_type: 'material', target_name: 'Robot Navigation System Clinical Guide', target_id: 'm007', timestamp: '2026-04-05T13:10:00Z' },
  { id: 'act011', actor_name: 'Admin', actor_id: null, action_type: 'create', target_type: 'material', target_name: 'Distributor Training Handbook 2024 Edition', target_id: 'm010', timestamp: '2026-04-05T11:45:00Z' },
  { id: 'act012', actor_name: 'Admin', actor_id: null, action_type: 'disable', target_type: 'agent', target_name: 'Fatima Nkosi', target_id: 'a008', timestamp: '2026-04-05T10:20:00Z' },
  { id: 'act013', actor_name: 'Erik Lindqvist', actor_id: 'a009', action_type: 'download', target_type: 'material', target_name: 'SinoCloud Platform Overview', target_id: 'm003', timestamp: '2026-04-04T16:50:00Z' },
  { id: 'act014', actor_name: 'Priya Sharma', actor_id: 'a006', action_type: 'download', target_type: 'material', target_name: 'Distributor Training Handbook 2024 Edition', target_id: 'm010', timestamp: '2026-04-04T14:15:00Z' },
  { id: 'act015', actor_name: 'Admin', actor_id: null, action_type: 'update', target_type: 'material', target_name: 'Veterinary CT Imaging Solutions Brochure', target_id: 'm009', timestamp: '2026-04-04T11:30:00Z' },
];

export const mockAdminElearning: AdminMaterial[] = [
  { id: 'ev1', title: 'InsitumCT 338 Product Overview & Key Features', type: 'Video', primaryCategory: 'products', category: 'CT Systems', modelCategory: 'InsitumCT 338', fileSize: '245 MB', uploadDate: '2026-03-20', uploadedBy: 'Admin', views: 3420, downloads: 0, previewAllowed: true, downloadAllowed: false, isVideo: true, videoDuration: '18:42', videoUrl: 'https://www.w3schools.com/html/mov_bbb.mp4', description: 'Comprehensive product overview covering key features, clinical applications, and technical highlights of the InsitumCT 338 system.', tags: ['CT Systems', 'Product Overview'], thumbnail: '' },
  { id: 'ev2', title: 'AlphaCT 868 Clinical Application Training — Module 1', type: 'Video', primaryCategory: 'products', category: 'CT Systems', modelCategory: 'AlphaCT 868', fileSize: '312 MB', uploadDate: '2026-03-15', uploadedBy: 'Admin', views: 2876, downloads: 0, previewAllowed: true, downloadAllowed: false, isVideo: true, videoDuration: '24:15', videoUrl: 'https://www.w3schools.com/html/mov_bbb.mp4', description: 'First module of the AlphaCT 868 clinical training series. Covers patient positioning, scan protocols, and image quality optimization.', tags: ['AlphaCT', 'Clinical Training'], thumbnail: '' },
  { id: 'ev3', title: 'SinoCloud Platform Walkthrough for Distributors', type: 'Video', primaryCategory: 'software', category: 'Software', modelCategory: 'SinoCloud Platform', fileSize: '198 MB', uploadDate: '2026-02-28', uploadedBy: 'Marketing', views: 1654, downloads: 0, previewAllowed: true, downloadAllowed: false, isVideo: true, videoDuration: '31:08', videoUrl: 'https://www.w3schools.com/html/mov_bbb.mp4', description: 'Step-by-step walkthrough of the SinoCloud platform for distributor onboarding. Covers account setup, image management, and reporting tools.', tags: ['SinoCloud', 'Software Training'], thumbnail: '' },
];
